import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  ListChecks,
  Briefcase,
} from 'lucide-react';

const statusMeta = {
  pending: { label: 'Pending', icon: Circle, color: 'text-gray-400' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-blue-500' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-green-600' },
};

export const ProjectProgress = () => {
  const { api, isCOO } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [tasksByJob, setTasksByJob] = useState({});
  const [busyTask, setBusyTask] = useState(null);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/project-management/progress-summary');
      setProjects(res.data || []);
    } catch (error) {
      console.error('Error fetching progress summary:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [api]);

  const loadTasks = async (jobId) => {
    try {
      const res = await api.get(`/project-management/jobs/${jobId}/tasks`);
      setTasksByJob((prev) => ({ ...prev, [jobId]: res.data.tasks || [] }));
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const toggleExpand = async (jobId) => {
    if (expanded === jobId) {
      setExpanded(null);
      return;
    }
    setExpanded(jobId);
    if (!tasksByJob[jobId]) {
      await loadTasks(jobId);
    }
  };

  const updateTaskStatus = async (jobId, task, newStatus) => {
    setBusyTask(task.id);
    try {
      await api.patch(`/project-management/tasks/${task.id}`, { status: newStatus });
      await loadTasks(jobId);
      fetchProjects();
      toast.success(`Task marked ${statusMeta[newStatus].label}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setBusyTask(null);
    }
  };

  const cycleStatus = (current) =>
    current === 'completed' ? 'pending' : current === 'in_progress' ? 'completed' : 'in_progress';

  const progressColor = (pct) =>
    pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-[#C6A87C]' : 'bg-blue-500';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-progress-page">
      <div>
        <h1 className="font-heading text-3xl flex items-center gap-3">
          <ListChecks className="w-7 h-7 text-[#C6A87C]" />
          Project Progress
        </h1>
        <p className="text-gray-500 mt-1">
          Track task completion across active projects. Updates here roll up into the project Gantt chart.
        </p>
      </div>

      {projects.length === 0 ? (
        <div className="bg-white border border-gray-100 p-12 text-center text-gray-400" data-testid="progress-empty">
          <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No active projects yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => {
            const isOpen = expanded === project.job_id;
            const tasks = tasksByJob[project.job_id] || [];
            return (
              <div
                key={project.job_id}
                className="bg-white border border-gray-100"
                data-testid={`progress-card-${project.job_id}`}
              >
                <button
                  onClick={() => toggleExpand(project.job_id)}
                  className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`progress-toggle-${project.job_id}`}
                >
                  {isOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-medium truncate">{project.client_name}</h3>
                      <span
                        className="font-heading text-xl text-gray-900 flex-shrink-0"
                        data-testid={`progress-pct-${project.job_id}`}
                      >
                        {project.progress_percent}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {project.project_type} {project.city ? `• ${project.city}` : ''}
                    </p>
                    <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${project.progress_percent}%` }}
                        className={`h-full ${progressColor(project.progress_percent)}`}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {project.completed_tasks} of {project.total_tasks} tasks completed
                    </p>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-5 py-4">
                    {tasks.length === 0 ? (
                      <p className="text-sm text-gray-400 py-2">
                        No tasks scheduled for this project yet.
                      </p>
                    ) : (
                      <div className="space-y-2" data-testid={`task-list-${project.job_id}`}>
                        {tasks.map((task) => {
                          const meta = statusMeta[task.status] || statusMeta.pending;
                          const Icon = meta.icon;
                          return (
                            <div
                              key={task.id}
                              className="flex items-center gap-3 p-3 bg-gray-50 rounded"
                              data-testid={`task-row-${task.id}`}
                            >
                              <button
                                disabled={busyTask === task.id}
                                onClick={() =>
                                  updateTaskStatus(
                                    project.job_id,
                                    task,
                                    task.status === 'completed' ? 'pending' : 'completed'
                                  )
                                }
                                className={`flex-shrink-0 ${meta.color} hover:scale-110 transition-transform disabled:opacity-50`}
                                title="Toggle complete"
                                data-testid={`task-complete-${task.id}`}
                              >
                                <Icon className="w-5 h-5" />
                              </button>
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-sm font-medium truncate ${
                                    task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'
                                  }`}
                                >
                                  {task.name}
                                </p>
                                {task.phase_name && (
                                  <p className="text-xs text-gray-400">{task.phase_name}</p>
                                )}
                              </div>
                              <button
                                disabled={busyTask === task.id}
                                onClick={() =>
                                  updateTaskStatus(project.job_id, task, cycleStatus(task.status))
                                }
                                className={`flex-shrink-0 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded border transition-colors disabled:opacity-50 ${meta.color} border-current/30 hover:bg-white`}
                                data-testid={`task-status-${task.id}`}
                              >
                                {meta.label}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectProgress;
